import React, { useEffect, useState } from 'react';
import { X, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';

const TRANSPORTISTAS = [
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'usps', label: 'USPS' },
  { value: 'amazon', label: 'Amazon Logistics' },
  { value: 'dhl', label: 'DHL' },
  { value: 'otro', label: 'Otro' },
];

const PAISES = [
  { value: 'usa', label: 'Estados Unidos (Miami)' },
  { value: 'china', label: 'China (Shenzhen)' },
];

// Mismo tope que PaqueteClienteSerializer.FACTURA_MAX_MB en el backend.
const FACTURA_MAX_MB = 10;

const VACIO = {
  tracking: '', transportista: 'ups', tienda: '', orden_o_factura: '',
  pais_origen: 'usa', bodega: '', descripcion: '', valor_declarado: '',
  moneda: 'USD', num_piezas: 1, tipo_empaque: '', instrucciones_especiales: '',
  desea_consolidar: 'no',
};

// Todos los campos son obligatorios — sobre todo la factura, la necesitamos
// para el trámite de nacionalización. Se valida acá Y en el backend
// (PaqueteClienteSerializer.validate), para que un POST directo a la API
// tampoco pueda saltarse el requisito.
export default function PrealertaModal({ onClose, onCreado }) {
  const [bodegas, setBodegas] = useState([]);
  const [datos, setDatos] = useState(VACIO);
  const [factura, setFactura] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.get('/bodegas/').then((res) => {
      const lista = res.data.results || res.data;
      setBodegas(lista);
      // Preselecciona la bodega según el país de origen por defecto (usa -> Miami).
      const porDefecto = lista.find((b) => b.pais === 'Estados Unidos') || lista[0];
      if (porDefecto) setDatos((d) => ({ ...d, bodega: porDefecto.id }));
    }).catch(() => toast.error('No se pudieron cargar las bodegas.'));
  }, []);

  const cambiarPais = (pais) => {
    const bodegaSugerida = bodegas.find((b) =>
      pais === 'usa' ? b.pais === 'Estados Unidos' : b.pais === 'China'
    );
    setDatos((d) => ({ ...d, pais_origen: pais, bodega: bodegaSugerida ? bodegaSugerida.id : d.bodega }));
  };

  const elegirFactura = (e) => {
    const archivo = e.target.files[0];
    if (archivo && archivo.size > FACTURA_MAX_MB * 1024 * 1024) {
      toast.error(`La factura pesa ${(archivo.size / (1024 * 1024)).toFixed(1)} MB y el máximo es ${FACTURA_MAX_MB} MB. Reduce su tamaño o envíala como foto.`);
      e.target.value = '';
      setFactura(null);
      return;
    }
    setFactura(archivo || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!datos.tracking.trim()) return toast.error('El número de tracking es obligatorio.');
    if (!datos.tienda.trim()) return toast.error('Indica la tienda donde compraste.');
    if (!datos.orden_o_factura.trim()) return toast.error('Indica el número de orden o factura.');
    if (!datos.bodega) return toast.error('Selecciona la bodega de origen.');
    if (!datos.descripcion.trim()) return toast.error('Describe el contenido del paquete.');
    if (!datos.valor_declarado) return toast.error('Indica el valor declarado del paquete.');
    if (!datos.tipo_empaque.trim()) return toast.error('Indica el tipo de empaque.');
    if (!factura) return toast.error('Adjunta la factura — la necesitamos para el trámite de nacionalización.');

    setEnviando(true);
    try {
      const form = new FormData();
      Object.entries(datos).forEach(([k, v]) => form.append(k, v));
      form.append('factura_adjunta', factura);

      const res = await api.post('/paquetes/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Prealerta creada. Te avisaremos cuando llegue a bodega.');
      onCreado(res.data);
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudo crear la prealerta.'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Nueva prealerta</h3>
          <button className="btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>
        <p className="text-secondary text-sm" style={{ marginBottom: 'var(--spacing-4)' }}>
          Avísanos de tu compra antes de que llegue a bodega. El resto de la información
          (peso, guía interna, fecha de llegada) la completamos nosotros cuando la recibamos.
        </p>

        <form onSubmit={handleSubmit} className="form-stack">
          <div className="form-row">
            <div>
              <label>Tracking *</label>
              <input value={datos.tracking} onChange={(e) => setDatos({ ...datos, tracking: e.target.value })} required />
            </div>
            <div>
              <label>Transportista *</label>
              <select value={datos.transportista} onChange={(e) => setDatos({ ...datos, transportista: e.target.value })} required>
                {TRANSPORTISTAS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div>
              <label>País de origen *</label>
              <select value={datos.pais_origen} onChange={(e) => cambiarPais(e.target.value)} required>
                {PAISES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label>Bodega *</label>
              <select value={datos.bodega} onChange={(e) => setDatos({ ...datos, bodega: e.target.value })} required>
                {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div>
              <label>Tienda *</label>
              <input value={datos.tienda} onChange={(e) => setDatos({ ...datos, tienda: e.target.value })} placeholder="Amazon, Shein, ..." required />
            </div>
            <div>
              <label>Orden / factura *</label>
              <input value={datos.orden_o_factura} onChange={(e) => setDatos({ ...datos, orden_o_factura: e.target.value })} required />
            </div>
          </div>

          <div>
            <label>Descripción del contenido *</label>
            <textarea rows={2} value={datos.descripcion} onChange={(e) => setDatos({ ...datos, descripcion: e.target.value })} required />
          </div>

          <div className="form-row">
            <div>
              <label>Valor declarado (USD) *</label>
              <input type="number" step="0.01" min="0.01" value={datos.valor_declarado}
                onChange={(e) => setDatos({ ...datos, valor_declarado: e.target.value })} required />
            </div>
            <div>
              <label>Piezas *</label>
              <input type="number" min="1" value={datos.num_piezas}
                onChange={(e) => setDatos({ ...datos, num_piezas: e.target.value })} required />
            </div>
            <div>
              <label>Tipo de empaque *</label>
              <input value={datos.tipo_empaque} onChange={(e) => setDatos({ ...datos, tipo_empaque: e.target.value })}
                placeholder="Caja, sobre, bolsa..." required />
            </div>
          </div>

          <div>
            <label>¿Deseas consolidar los paquetes? *</label>
            <select value={datos.desea_consolidar} onChange={(e) => setDatos({ ...datos, desea_consolidar: e.target.value })} required>
              <option value="no">No - Enviar en cuanto llegue</option>
              <option value="si">Sí - Esperar otros paquetes (Costo extra)</option>
            </select>
          </div>

          <div>
            <label>Instrucciones especiales</label>
            <textarea rows={2} value={datos.instrucciones_especiales}
              onChange={(e) => setDatos({ ...datos, instrucciones_especiales: e.target.value })}
              placeholder="Paquete frágil, esperar otros paquetes, reempacar, etc." />
          </div>

          <div>
            <label>Factura *</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={elegirFactura} required />
            <p className="text-xs text-secondary" style={{ marginTop: '4px' }}>
              Es obligatoria: la necesitamos para el trámite de nacionalización.
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              <Send size={16} /> <span>{enviando ? 'Enviando...' : 'Crear prealerta'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
